import TRAIcon from "../icons/TRAIcon";
import { TRASkeleton, TRASpinner } from "../components/primitives/TRALayoutPrimitives";
import styles from "./shell.module.css";
import { cn } from "../utils/cn";

export function EmptyState({
  title = "Nothing here yet",
  description,
  action,
  icon = "sparkles",
  className,
}) {
  return (
    <div className={cn(styles.stateFrame, className)} role="status">
      <TRAIcon name={icon} size="lg" className={styles.stateIcon} />
      <h3 className={styles.stateTitle}>{title}</h3>
      {description ? <p className={styles.stateBody}>{description}</p> : null}
      {action}
    </div>
  );
}

/**
 * Polished loading placeholders.
 * @param {"skeleton"|"spinner"} [variant]
 * @param {"default"|"profile"|"matchDetail"|"list"|"chart"} [layout]
 */
export function LoadingState({
  label = "Loading",
  variant = "skeleton",
  layout = "default",
  className,
}) {
  if (variant === "spinner") {
    return (
      <div className={cn(styles.stateFrame, className)}>
        <TRASpinner label={label} />
      </div>
    );
  }

  let body = (
    <>
      <TRASkeleton style={{ height: 24, width: "40%" }} />
      <TRASkeleton style={{ height: 120, width: "100%" }} />
      <TRASkeleton style={{ height: 80, width: "100%" }} />
    </>
  );

  if (layout === "profile") {
    body = (
      <>
        <div className={styles.skelProfileHead}>
          <TRASkeleton className={styles.skelAvatar} />
          <div className={styles.skelProfileMeta}>
            <TRASkeleton style={{ height: 22, width: "55%" }} />
            <TRASkeleton style={{ height: 14, width: "38%" }} />
          </div>
        </div>
        <TRASkeleton style={{ height: 56, width: "42%" }} />
        <TRASkeleton style={{ height: 88, width: "100%" }} />
        <TRASkeleton style={{ height: 120, width: "100%" }} />
        <TRASkeleton style={{ height: 72, width: "100%" }} />
        <TRASkeleton style={{ height: 72, width: "100%" }} />
      </>
    );
  } else if (layout === "matchDetail") {
    body = (
      <>
        <TRASkeleton style={{ height: 36, width: "70%" }} />
        <TRASkeleton style={{ height: 112, width: "100%" }} />
        <TRASkeleton style={{ height: 72, width: "100%" }} />
        <div className={styles.skelChartRow}>
          <TRASkeleton style={{ height: 148, width: "100%" }} />
        </div>
        <TRASkeleton style={{ height: 64, width: "100%" }} />
        <TRASkeleton style={{ height: 64, width: "100%" }} />
      </>
    );
  } else if (layout === "list") {
    body = (
      <>
        <TRASkeleton style={{ height: 52, width: "100%" }} />
        <TRASkeleton style={{ height: 52, width: "100%" }} />
        <TRASkeleton style={{ height: 52, width: "100%" }} />
        <TRASkeleton style={{ height: 52, width: "100%" }} />
      </>
    );
  } else if (layout === "chart") {
    body = (
      <>
        <TRASkeleton style={{ height: 18, width: "36%" }} />
        <TRASkeleton style={{ height: 160, width: "100%" }} />
        <TRASkeleton style={{ height: 14, width: "48%" }} />
      </>
    );
  }

  return (
    <div
      className={cn(styles.stateFrame, styles.skeletonStack, className)}
      role="status"
      aria-label={label}
      aria-busy="true"
      data-loading-layout={layout}
    >
      {body}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description = "Please try again. If it keeps happening, refresh the page.",
  action,
  className,
}) {
  return (
    <div className={cn(styles.stateFrame, styles.stateError, className)} role="alert">
      <TRAIcon name="alert" size="lg" className={styles.stateIcon} />
      <h3 className={styles.stateTitle}>{title}</h3>
      {description ? <p className={styles.stateBody}>{description}</p> : null}
      {action}
    </div>
  );
}

/**
 * Friendly offline / connectivity message for platform surfaces.
 */
export function OfflineState({
  title = "You’re offline",
  description = "Showing what we already have on this device. Reconnect to refresh.",
  action,
  className,
}) {
  return (
    <div className={cn(styles.stateFrame, styles.stateOffline, className)} role="status">
      <TRAIcon name="wifiOff" size="lg" className={styles.stateIcon} />
      <h3 className={styles.stateTitle}>{title}</h3>
      {description ? <p className={styles.stateBody}>{description}</p> : null}
      {action}
    </div>
  );
}
